import { codeInputBox } from '@app/forms/components/CodeInput/CodeInput.sx';
import { Box } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import React, { SyntheticEvent, useEffect, useRef } from 'react';

export interface Props {
  length: number;
  value: string;
  onChange: (value: string) => void;
  sx?: SxProps<Theme>;
}

const CodeInput = ({ length, value, onChange, sx }: Props): JSX.Element => {
  const inputRefs = useRef([]);

  useEffect(() => {
    inputRefs.current[0].focus();
  }, []);

  let elements = value.slice(0, length).split('');

  if (value.length < length) {
    elements = [...elements, ...Array(length - value.length).fill('')];
  }

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowLeft':
        if (index > 0) {
          inputRefs.current[index - 1].focus();
        }
        break;
      case 'ArrowRight':
        if (index < length - 1 && value[index]) {
          inputRefs.current[index + 1].focus();
        }
        break;
      case 'Backspace':
        event.preventDefault();
        if (value[index]?.match(/^[0-9]$/)) {
          const elements = [...value];
          elements[index] = '';
          onChange(elements.join(''));
        } else {
          if (index > 0) {
            inputRefs.current[index - 1].focus();
          }
        }
        break;
    }
  };

  const handleChange = (index: number, newValue: string) => {
    if (newValue.length > 2) {
      return;
    }

    const oldElement = elements[index];
    const newElement = newValue.trim().replace(oldElement, '');
    if (newElement < '0' || newElement > '9') {
      return;
    }
    const nextValue = [...value];
    nextValue[index] = newElement;

    onChange(nextValue.slice(0, 6).join(''));
    if (index < length - 1) {
      inputRefs.current[index + 1].focus();
    } else {
      inputRefs.current[index].blur();
    }
  };

  const handlePaste = async (event: SyntheticEvent<HTMLInputElement>) => {
    const data = await navigator.clipboard.readText();
    if (/^\d+$/.test(data)) {
      onChange(data.slice(0, length));
      (event.target as HTMLElement).blur();
    }
  };

  return (
    <Box sx={{ ...codeInputBox, ...sx }}>
      {elements.map((element, index) => (
        <input
          type="number"
          key={index}
          value={element}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onChange={(event) => handleChange(index, event.target.value)}
          onPaste={handlePaste}
          ref={(element) => (inputRefs.current[index] = element)}
          pattern="\d*"
        />
      ))}
    </Box>
  );
};

export default CodeInput;
